import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ArrowRight, Sparkles, Star } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { authPathWithPlan, mapPricingPlans } from './sharedPricing';
import {
  dashboardMetrics,
  finalCta,
  flowSteps,
  heroDescription,
  heroEyebrow,
  heroTitleLines,
  productHighlights,
  statsBar,
  testimonials,
} from './landingCopy';

export default function LandingBold({
  brand = 'Workspace',
  logoUrl = null,
  pricingPlans = [],
  authPath = '/auth',
  primaryColor = '#f97316',
} = {}) {
  const navigate = useNavigate();
  const plans = mapPricingPlans(pricingPlans);
  const title = heroTitleLines();
  const products = productHighlights(brand);
  const steps = flowSteps(brand);
  const quotes = testimonials(brand);
  const cta = finalCta(brand);
  const featuredKey = plans.find((p) => p.highlight)?.key || plans[0]?.key;

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="min-h-screen bg-black text-white" data-testid="wl-landing-bold">
      <nav className="fixed top-0 inset-x-0 z-40 border-b border-white/10 bg-black/80 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {logoUrl ? <img src={logoUrl} alt={brand} className="w-8 h-8 rounded-lg object-cover" /> : <Sparkles className="w-6 h-6" style={{ color: primaryColor }} />}
            <span className="font-bold text-lg tracking-tight">{brand}</span>
          </div>
          <div className="hidden md:flex items-center gap-6 text-sm text-zinc-400">
            <button type="button" onClick={() => scrollTo('products')} className="hover:text-white">Products</button>
            <button type="button" onClick={() => scrollTo('flow')} className="hover:text-white">How it works</button>
            <button type="button" onClick={() => scrollTo('pricing')} className="hover:text-white">Pricing</button>
          </div>
          <Button onClick={() => navigate(authPath)} className="rounded-full text-black font-bold" style={{ background: primaryColor }}>
            Get started
          </Button>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-end pb-16 pt-32 px-6 overflow-hidden">
        <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse 80% 60% at 50% 0%, ${primaryColor}55, transparent 70%), linear-gradient(180deg, #0a0a0a, #000)` }} />
        <div className="relative z-10 max-w-5xl mx-auto w-full">
          <p className="inline-flex items-center gap-2 text-sm font-medium mb-6 px-3 py-1.5 rounded-full border border-white/15 bg-white/5" style={{ color: primaryColor }}>
            <Sparkles className="w-4 h-4" /> {heroEyebrow()}
          </p>
          <h1 className="text-5xl md:text-7xl font-extrabold leading-[0.95] tracking-tight mb-6">
            <span className="block text-white">{title.line1}</span>
            <span className="block mt-2" style={{ color: primaryColor }}>{title.line2}</span>
          </h1>
          <p className="text-lg text-zinc-400 max-w-2xl mb-8 leading-relaxed">
            {heroDescription(brand)}
          </p>
          <div className="flex flex-wrap gap-4 mb-12">
            <Button
              onClick={() => navigate(authPathWithPlan(authPath, featuredKey))}
              className="rounded-full px-8 py-6 text-base font-bold text-black"
              style={{ background: primaryColor }}
            >
              Start for free <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            <button type="button" onClick={() => scrollTo('flow')} className="text-zinc-400 hover:text-white text-sm">
              See how it works ↓
            </button>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 grid grid-cols-2 md:grid-cols-5 gap-3">
            {dashboardMetrics().map((m) => (
              <div key={m.label} className="rounded-xl bg-black/40 border border-white/10 p-3">
                <p className="text-[11px] text-zinc-500">{m.label}</p>
                <p className="text-lg font-bold text-white">{m.value}</p>
                <p className="text-[11px]" style={{ color: primaryColor }}>{m.change}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 px-6 border-y border-white/10 bg-white/[0.02]">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {statsBar().map((s) => (
            <div key={s.label}>
              <p className="text-3xl font-extrabold" style={{ color: primaryColor }}>{s.value}</p>
              <p className="text-sm text-zinc-500 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Products */}
      <section id="products" className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: primaryColor }}>Products</p>
          <h2 className="text-3xl md:text-5xl font-extrabold mb-4">Everything you need to launch and grow</h2>
          <p className="text-zinc-400 text-lg max-w-2xl mb-12">
            From websites and AI tools to marketing and lead generation — {brand} puts your whole stack in one place.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {products.map((p) => (
              <div key={p.title} className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 hover:border-white/20 transition-colors">
                <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: primaryColor }}>{p.tag}</p>
                <h3 className="text-xl font-bold mb-1">{p.title}</h3>
                <p className="text-sm mb-3" style={{ color: primaryColor }}>{p.subtitle}</p>
                <p className="text-sm text-zinc-400 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="flow" className="py-24 px-6 bg-white/[0.02] border-y border-white/10">
        <div className="max-w-6xl mx-auto">
          <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: primaryColor }}>How it works</p>
          <h2 className="text-3xl md:text-5xl font-extrabold mb-12">Your connected growth journey</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {steps.map((s) => (
              <div key={s.step} className="rounded-2xl border border-white/10 p-6">
                <p className="text-4xl font-extrabold opacity-30 mb-3" style={{ color: primaryColor }}>{s.step}</p>
                <h3 className="text-lg font-bold mb-1">{s.title}</h3>
                <p className="text-xs font-semibold mb-2" style={{ color: primaryColor }}>{s.module}</p>
                <p className="text-sm text-zinc-400 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-extrabold mb-4 text-center">Start free. Scale as you grow.</h2>
          <p className="text-zinc-400 text-center mb-12 max-w-xl mx-auto">
            Pick a plan — modules unlock the moment you create your account.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <div
                key={plan.key || plan.name}
                className={`rounded-3xl p-8 flex flex-col border ${plan.highlight ? 'border-transparent' : 'border-white/10 bg-white/[0.03]'}`}
                style={plan.highlight ? { background: `linear-gradient(180deg, ${primaryColor}33, transparent)`, boxShadow: `0 0 40px ${primaryColor}33` } : undefined}
              >
                {plan.badge && (
                  <span className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: primaryColor }}>{plan.badge}</span>
                )}
                <h3 className="text-xl font-bold mb-1">{plan.name}</h3>
                <p className="text-zinc-500 text-sm mb-4">{plan.desc}</p>
                <div className="text-4xl font-extrabold mb-6">{plan.price}</div>
                <ul className="space-y-2 flex-1 mb-8">
                  {(plan.features || []).slice(0, 8).map((f) => (
                    <li key={f} className="flex gap-2 text-sm text-zinc-300">
                      <Check className="w-4 h-4 shrink-0 mt-0.5" style={{ color: primaryColor }} />
                      {f}
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => navigate(authPathWithPlan(authPath, plan.key))}
                  className={`w-full rounded-full font-bold ${plan.highlight ? 'text-black' : 'bg-white/10 hover:bg-white/20 text-white'}`}
                  style={plan.highlight ? { background: primaryColor } : undefined}
                >
                  {plan.cta}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-6 border-y border-white/10 bg-white/[0.02]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-extrabold text-center mb-3">Loved by builders worldwide</h2>
          <p className="text-center text-zinc-500 text-sm mb-10 flex items-center justify-center gap-1">
            {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />)}
            <span className="ml-2">4.9 / 5 from 2,400+ reviews</span>
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {quotes.map((t) => (
              <div key={t.name} className="rounded-2xl border border-white/10 bg-black/40 p-6">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => <Star key={j} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />)}
                </div>
                <p className="text-sm text-zinc-300 leading-relaxed mb-6">&ldquo;{t.quote}&rdquo;</p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-black" style={{ background: primaryColor }}>{t.avatar}</div>
                  <div>
                    <p className="text-sm font-semibold">{t.name}</p>
                    <p className="text-xs text-zinc-500">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-28 px-6 text-center">
        <h2 className="text-4xl md:text-5xl font-extrabold mb-4 max-w-3xl mx-auto">{cta.title}</h2>
        <p className="text-zinc-400 text-lg mb-8">{cta.subtitle}</p>
        <Button
          onClick={() => navigate(authPathWithPlan(authPath, featuredKey))}
          className="rounded-full px-10 py-6 text-lg font-bold text-black"
          style={{ background: primaryColor }}
        >
          Get started free <ArrowRight className="ml-2 w-5 h-5" />
        </Button>
        <p className="text-zinc-500 text-sm mt-4">{cta.note}</p>
      </section>

      <footer className="border-t border-white/10 py-10 px-6 text-center text-sm text-zinc-500">
        © {new Date().getFullYear()} {brand}
      </footer>
    </div>
  );
}
